import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandLogo from '@/components/BrandLogo';
import {
  ArrowRight,
  Brain,
  Building2,
  Check,
  ChevronRight,
  Landmark,
  Languages,
  Mic,
  Moon,
  PlayCircle,
  Scale,
  Search,
  ShieldCheck,
  Sun,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';

const CONTAINER = 'mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8';

const SOCIAL_PROOF_BADGES = [
  'Indian Kanoon Integration',
  'Multilingual',
  'Adversarial AI',
  'eCourts Ready',
];

const DEMO_SCENARIOS = [
  {
    prompt:
      'Review our interim application, identify procedural weaknesses opposing counsel would attack, and prepare hearing notes for tomorrow in Hindi.',
    title: 'Aina pressure-tests the filing before the hearing.',
    summary:
      'Procedural gaps, missing authorities, and rebuttal angles surface inside the same matter record before the other side can exploit them.',
    matter: 'Sharma Infra v. Metro Transit Authority',
    forum: 'Delhi High Court',
    status: [
      { label: 'Aina live' },
      { label: 'Hindi ready' },
      { label: 'Bench brief' },
    ],
    metrics: [
      { label: 'Risk flags', value: '03' },
      { label: 'Authorities', value: '12' },
      { label: 'Bench note', value: 'Ready' },
    ],
    workspace: [
      {
        label: 'Adversarial map',
        desc: 'Vulnerability clusters show where procedure, pleading gaps, and relief framing can be attacked.',
      },
      {
        label: 'Matter memory',
        desc: 'Orders, filings, hearing notes, and team instructions remain attached to the same live matter.',
      },
      {
        label: 'Partner view',
        desc: 'Senior counsel sees the practical next move, not just a long research dump.',
      },
    ],
  },
  {
    prompt:
      'Turn this client conference and dictation into a Section 138 response plan, keep the Tamil client phrasing, and draft the first notice.',
    title: 'Jolly turns voice into chamber-ready legal work.',
    summary:
      'Client calls, dictation, and post-hearing debriefs become structured matter memory, draft inputs, and follow-up tasks in minutes.',
    matter: 'Rajan Exports v. Horizon Suppliers',
    forum: 'Madras High Court',
    status: [
      { label: 'Voice synced' },
      { label: 'Tamil kept' },
      { label: 'Drafting live' },
    ],
    metrics: [
      { label: 'Minutes captured', value: '47' },
      { label: 'Tasks created', value: '08' },
      { label: 'Draft status', value: 'Live' },
    ],
    workspace: [
      {
        label: 'Voice capture',
        desc: 'Long dictation and scattered debriefs convert into clean chronology, tasks, and issue lists.',
      },
      {
        label: 'Multilingual output',
        desc: 'Client-facing language and court-facing drafting can diverge without losing nuance.',
      },
      {
        label: 'Matter timeline',
        desc: 'Every hearing, call, and draft stays visible to the whole team in one place.',
      },
    ],
  },
  {
    prompt:
      "Summarise this judge's recent orders in arbitration matters, show likely objections, and tell me which live matters in the firm are exposed.",
    title: 'Judge intelligence turns research into strategic timing.',
    summary:
      'Bench patterns, recent orders, and matter-level implications land in one dashboard so partners can decide faster and brief sharper.',
    matter: 'Apex Buildcon v. Eastern Power Limited',
    forum: 'Bombay High Court',
    status: [
      { label: 'Judge mapped' },
      { label: 'Matter linked' },
      { label: 'Fresh law' },
    ],
    metrics: [
      { label: 'Orders scanned', value: '26' },
      { label: 'Live matters', value: '05' },
      { label: 'Objections', value: '04' },
    ],
    workspace: [
      {
        label: 'Bench patterns',
        desc: 'Recent reasoning, procedural preferences, and favored lines of questioning become visible.',
      },
      {
        label: 'Case relevance',
        desc: 'Fresh judgments are pushed only to the matters and partners who need them.',
      },
      {
        label: 'Hearing readiness',
        desc: 'Briefing packs stay connected to forum dynamics instead of generic research notes.',
      },
    ],
  },
];

const PILLARS = [
  {
    name: 'Matter OS',
    icon: Scale,
    title: 'Every matter, end to end',
    body: 'Lifecycle tracking, eCourts sync, deadline engine, client CRM. Know the status of every case at a glance.',
  },
  {
    name: 'Intelligence Suite',
    icon: Brain,
    title: 'AI that knows Indian law',
    body: "Research against Indian Kanoon, stress-test your case with Aina's adversarial lens, prep for the bench — all in Hindi.",
    accent: true,
  },
  {
    name: 'Firm Layer',
    icon: Building2,
    title: 'Built for firms, not just lawyers',
    body: 'GST-compliant billing, precedent bank, team roles, partner analytics. The tools firms actually need.',
  },
];

const SPOTLIGHT_ROWS = [
  {
    id: 'aina',
    title: 'Aina — Adversarial Intelligence',
    eyebrow: 'Find the weakness before the other side does',
    description:
      'Aina is built for the moments where the filing looks polished, but the risk is hiding in procedure, sequence, or bench pressure.',
    bullets: [
      'Surfaces procedural exposure before filing or hearing day',
      'Generates likely opposing counsel attacks and rebuttal lines',
      'Produces issue lists, weak prayers, and missing-authority warnings',
      'Keeps every insight tied to the matter, bench, and draft history',
    ],
    visual: 'quote',
    reverse: false,
  },
  {
    id: 'jolly',
    title: 'Jolly — Voice-First Legal Capture',
    eyebrow: 'From dictation to structured work product',
    description:
      'Jolly captures what lawyers actually do in practice: dictate after conferences, speak after hearings, and move quickly when there is no time to type.',
    bullets: [
      'Records client calls, hearing debriefs, and chamber dictation',
      'Preserves English, Hindi, Tamil, and Telugu in the same workflow',
      'Turns speech into matter notes, tasks, and first-draft inputs',
      'Pushes structured output back into the live matter timeline',
    ],
    visual: 'voice',
    reverse: true,
  },
  {
    id: 'judge-intelligence',
    title: 'Judge Intelligence',
    eyebrow: 'Bench-aware preparation, not generic research',
    description:
      'Judge intelligence connects recent orders, judicial tendencies, and similar matters to the file in front of you, so hearing strategy becomes clearer earlier.',
    bullets: [
      'Maps recent judicial patterns across similar case types',
      'Flags recurring objections, timelines, and procedural preferences',
      'Finds which active matters are affected by fresh developments',
      'Creates bench-aware briefing packs for partners and teams',
    ],
    visual: 'judge',
    reverse: false,
  },
];

const PRO_FEATURES = [
  '14-day free trial',
  'Aina and judge intelligence',
  'Matter OS — lifecycle, deadlines, eCourts sync',
  'Voice capture with Jolly — multilingual',
  'Research against Indian Kanoon',
  'Practice analytics and workflow depth',
];

const getLongestText = (items, key) =>
  items.reduce((longest, item) => (item[key].length > longest.length ? item[key] : longest), '');

const LONGEST_PROMPT = getLongestText(DEMO_SCENARIOS, 'prompt');
const LONGEST_TITLE = getLongestText(DEMO_SCENARIOS, 'title');
const LONGEST_SUMMARY = getLongestText(DEMO_SCENARIOS, 'summary');

const WAVE_HEIGHTS = ['h-8','h-14','h-10','h-16','h-9','h-14','h-7','h-12','h-8','h-14','h-10','h-11'];

function SectionHeader({ eyebrow, title, body, dark }) {
  return (
    <div className="max-w-3xl">
      <p className="text-xs font-semibold uppercase tracking-[0.32em] text-[#C9A84C]">
        {eyebrow}
      </p>
      <h2
        className={`mt-4 text-3xl font-semibold tracking-[-0.05em] sm:text-4xl ${
          dark ? 'text-[#FAFAFA]' : 'text-[#0A0A0A]'
        }`}
      >
        {title}
      </h2>
      <p className={`mt-4 text-base leading-8 ${dark ? 'text-[#A0A0A0]' : 'text-[#6B6B6B]'}`}>{body}</p>
    </div>
  );
}

function DemoWindow({ demoRef, activeDemo, typedPrompt, dark }) {
  const statusClass = dark
    ? 'border-[#2A2A2A] bg-[#1C1C1C] text-[#C0C0C0]'
    : 'border-[#2A2A2A] bg-[#1C1C1C] text-[#C0C0C0]';

  return (
    <div id="live-demo" ref={demoRef} className="relative mx-auto w-full max-w-[640px] [overflow-anchor:none]">
      <div
        className={`relative min-h-[680px] overflow-hidden rounded-2xl bg-[#08111d] lg:min-h-0 lg:aspect-[16/10] ${
          dark
            ? 'border border-white/10 shadow-[0_24px_80px_rgba(0,0,0,0.45)]'
            : 'border border-[#E0E0DC] shadow-[0_24px_80px_rgba(0,0,0,0.12)]'
        }`}
      >
        <div className="relative flex h-full flex-col">
          <div className="flex items-center justify-between border-b border-white/10 bg-black/10 px-4 py-3 sm:px-5">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
            </div>
            <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-300">
              Matter Dashboard
            </div>
            <div className="rounded-full border border-[#C9A84C]/30 bg-[#C9A84C]/12 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#F4E6B1]">
              Live matter
            </div>
          </div>

          <div className="grid flex-1 gap-4 p-4 sm:p-6 lg:grid-cols-[1.18fr_0.82fr] lg:items-stretch lg:gap-4 lg:p-5">
            <div className="flex flex-col gap-4">
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-400">Active matter</p>
                <p className="mt-2 text-lg font-semibold tracking-[-0.03em] text-white">{activeDemo.matter}</p>
                <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/10 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.22em] text-slate-300">
                  <Landmark className="h-3.5 w-3.5" />
                  {activeDemo.forum}
                </div>
              </div>

              <div className="flex min-h-[220px] flex-col rounded-xl border border-white/10 bg-black/20 p-4 lg:flex-1">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-400">Live counsel brief</p>
                  <div className="rounded-full border border-white/10 bg-black/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-300">
                    Streaming
                  </div>
                </div>

                <div className="relative mt-3 flex-1 min-h-[148px] text-sm leading-6 text-slate-200 lg:min-h-[168px]">
                  <p className="invisible whitespace-pre-wrap pr-3">{LONGEST_PROMPT}</p>
                  <p className="absolute inset-0 whitespace-pre-wrap pr-3">
                    {typedPrompt}
                    <span className="ml-0.5 inline-block h-4 w-px animate-pulse bg-[#C9A84C] align-[-2px]" aria-hidden="true" />
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="relative">
                  <p className="invisible max-w-[26rem] pr-3 text-lg font-semibold tracking-[-0.04em] lg:text-xl">{LONGEST_TITLE}</p>
                  <p className="absolute inset-0 max-w-[26rem] pr-3 text-lg font-semibold tracking-[-0.04em] text-white lg:text-xl">
                    {activeDemo.title}
                  </p>
                </div>
                <div className="relative mt-3 text-sm leading-6 text-slate-300">
                  <p className="invisible max-w-[32rem] pr-3">{LONGEST_SUMMARY}</p>
                  <p className="absolute inset-0 max-w-[32rem] pr-3">{activeDemo.summary}</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <div className="rounded-xl border border-white/10 bg-black/10 p-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-400">Matter signals</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {activeDemo.status.map((item) => (
                    <span
                      key={item.label}
                      className={`rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] ${statusClass}`}
                    >
                      {item.label}
                    </span>
                  ))}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {activeDemo.metrics.map((metric) => (
                    <div
                      key={metric.label}
                      className="flex min-w-0 min-h-[88px] flex-col items-center justify-center overflow-hidden rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-center"
                    >
                      <p className="text-[11px] leading-4 text-slate-400">
                        {metric.label}
                      </p>
                      <p className="mt-2 text-base font-semibold tracking-[-0.03em] text-white">{metric.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid auto-rows-fr gap-3 lg:flex-1">
                {activeDemo.workspace.map((item) => (
                  <div key={item.label} className="rounded-xl border border-white/10 bg-black/20 p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#C9A84C]">{item.label}</p>
                    <p className="mt-3 text-sm leading-6 text-slate-300">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SpotlightVisual({ type, dark }) {
  const outerClass = dark
    ? 'border-[#242424] bg-[#141414]'
    : 'border-[#E8E8E5] bg-[#F7F7F5]';
  const innerCard = dark
    ? 'border-[#2A2A2A] bg-[#1C1C1C]'
    : 'border-[#E0E0DC] bg-white';
  const textMuted = dark ? 'text-[#606060]' : 'text-[#9B9B9B]';
  const textBody = dark ? 'text-[#A0A0A0]' : 'text-[#6B6B6B]';
  const textPrimary = dark ? 'text-[#FAFAFA]' : 'text-[#0A0A0A]';
  const shadow = dark ? 'shadow-[0_24px_80px_rgba(0,0,0,0.28)]' : '';

  const badgeClass = dark
    ? 'border-[#2A2A2A] bg-[#1C1C1C] text-[#A0A0A0]'
    : 'border-[#E0E0DC] bg-[#F0F0ED] text-[#3A3A3A]';

  if (type === 'quote') {
    return (
      <div className={`rounded-2xl border p-8 ${outerClass} ${shadow}`}>
        <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] border-[#C9A84C]/25 bg-[#C9A84C]/10 text-[#C9A84C]`}>
          <ShieldCheck className="h-3.5 w-3.5" />
          Field signal
        </div>
        <blockquote className={`mt-6 text-2xl font-semibold leading-10 tracking-[-0.04em] sm:text-[2rem] ${textPrimary}`}>
          "Aina found 3 procedural risks in our IA that opposing counsel would have exploited. We fixed them before the hearing."
        </blockquote>
        <p className={`mt-6 text-sm uppercase tracking-[0.24em] ${textMuted}`}>Senior Advocate, Delhi High Court</p>
      </div>
    );
  }

  if (type === 'voice') {
    return (
      <div className={`overflow-hidden rounded-2xl border ${outerClass} ${shadow}`}>
        <div className={`border-b px-6 py-4 ${dark ? 'border-[#242424]' : 'border-[#E8E8E5]'}`}>
          <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] ${badgeClass}`}>
            <Mic className="h-3.5 w-3.5" />
            Jolly capture
          </div>
        </div>
        <div className="space-y-5 px-6 py-6">
          <div className={`rounded-xl border p-4 ${innerCard}`}>
            <p className={`text-[11px] uppercase tracking-[0.24em] ${textMuted}`}>Hearing debrief</p>
            <div className="mt-4 flex items-end gap-1.5">
              {WAVE_HEIGHTS.map((h, i) => (
                <span
                  key={i}
                  className={`w-2 rounded-full ${h} ${
                    i % 3 === 0
                      ? dark ? 'bg-[#FAFAFA]' : 'bg-[#0A0A0A]'
                      : dark ? 'bg-[#444444]' : 'bg-[#C0C0C0]'
                  }`}
                />
              ))}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className={`rounded-xl border p-4 ${innerCard}`}>
              <p className={`text-[11px] uppercase tracking-[0.24em] ${textMuted}`}>Structured notes</p>
              <ul className="mt-3 space-y-2 text-sm">
                <li className={`flex items-start gap-2 ${textBody}`}>
                  <Check className="mt-0.5 h-4 w-4 flex-none text-[#C9A84C]" />
                  Limitation concern flagged for client confirmation
                </li>
                <li className={`flex items-start gap-2 ${textBody}`}>
                  <Check className="mt-0.5 h-4 w-4 flex-none text-[#C9A84C]" />
                  Tamil client update retained for follow-up
                </li>
              </ul>
            </div>
            <div className={`rounded-xl border p-4 ${innerCard}`}>
              <p className={`text-[11px] uppercase tracking-[0.24em] ${textMuted}`}>Matter actions</p>
              <ul className="mt-3 space-y-2 text-sm">
                <li className={`flex items-start gap-2 ${textBody}`}>
                  <ChevronRight className="mt-0.5 h-4 w-4 flex-none text-[#C9A84C]" />
                  Draft first notice
                </li>
                <li className={`flex items-start gap-2 ${textBody}`}>
                  <ChevronRight className="mt-0.5 h-4 w-4 flex-none text-[#C9A84C]" />
                  Schedule filing checklist
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // judge
  return (
    <div className={`overflow-hidden rounded-2xl border ${outerClass} ${shadow}`}>
      <div className={`border-b px-6 py-4 ${dark ? 'border-[#242424]' : 'border-[#E8E8E5]'}`}>
        <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] ${badgeClass}`}>
          <Search className="h-3.5 w-3.5" />
          Judge intelligence
        </div>
      </div>
      <div className="grid gap-3 px-6 py-6 sm:grid-cols-3">
        {[
          { label: 'Recent orders', value: '26', body: 'Arbitration matters scanned for forum pattern recognition.' },
          { label: 'Recurring objections', value: '04', body: 'Maintainability, delay, documentary gaps, and forum strategy.' },
          { label: 'Live matters linked', value: '05', body: 'Fresh judicial movement pushed directly to exposed matters.' },
        ].map((stat) => (
          <div key={stat.label} className={`rounded-xl border p-4 ${innerCard}`}>
            <p className={`text-[11px] uppercase tracking-[0.24em] ${textMuted}`}>{stat.label}</p>
            <p className={`mt-3 text-3xl font-semibold tracking-[-0.04em] ${textPrimary}`}>{stat.value}</p>
            <p className={`mt-2 text-sm ${textBody}`}>{stat.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const dark = theme === 'dark';

  const demoRef = useRef(null);
  const [demoIndex, setDemoIndex] = useState(0);
  const [typedCount, setTypedCount] = useState(0);
  const [isDemoInView, setIsDemoInView] = useState(true);

  const activeDemo = DEMO_SCENARIOS[demoIndex];
  const typedPrompt = activeDemo.prompt.slice(0, typedCount);

  useEffect(() => {
    if (user?.onboarding_done) {
      navigate('/today', { replace: true });
    }
  }, [navigate, user?.onboarding_done]);

  useEffect(() => {
    if (!demoRef.current || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setIsDemoInView(entry.isIntersecting),
      { threshold: 0.2, rootMargin: '120px 0px 120px 0px' },
    );
    observer.observe(demoRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isDemoInView) return undefined;
    if (typedCount < activeDemo.prompt.length) {
      const id = window.setTimeout(() => setTypedCount((c) => c + 1), 22);
      return () => window.clearTimeout(id);
    }
    const id = window.setTimeout(() => {
      setDemoIndex((i) => (i + 1) % DEMO_SCENARIOS.length);
      setTypedCount(0);
    }, 1800);
    return () => window.clearTimeout(id);
  }, [activeDemo.prompt, isDemoInView, typedCount]);

  if (user?.onboarding_done) return null;

  // theme-aware tokens
  const pageBg    = dark ? 'bg-[#0A0A0A] text-[#FAFAFA]' : 'bg-white text-[#0A0A0A]';
  const altBg     = dark ? 'bg-[#111111]' : 'bg-[#F7F7F5]';
  const border    = dark ? 'border-[#1E1E1E]' : 'border-[#E8E8E5]';
  const cardBg    = dark ? 'bg-[#141414] border-[#242424]' : 'bg-white border-[#E8E8E5]';
  const navBg     = dark ? 'bg-[#0A0A0A]/90 border-[#1E1E1E]' : 'bg-white/90 border-[#E8E8E5]';
  const textPri   = dark ? 'text-[#FAFAFA]' : 'text-[#0A0A0A]';
  const textSec   = dark ? 'text-[#A0A0A0]' : 'text-[#6B6B6B]';
  const textMuted = dark ? 'text-[#606060]' : 'text-[#9B9B9B]';
  const badge     = dark ? 'border-[#2A2A2A] bg-[#1C1C1C] text-[#A0A0A0]' : 'border-[#E0E0DC] bg-[#F0F0ED] text-[#3A3A3A]';
  const ghostBtn  = dark
    ? 'border-[#2A2A2A] bg-[#141414] text-[#FAFAFA] hover:bg-[#1C1C1C]'
    : 'border-[#E0E0DC] bg-white text-[#0A0A0A] hover:bg-[#F7F7F5]';
  const toggleBtn = dark
    ? 'border-[#2A2A2A] bg-[#1C1C1C] text-[#A0A0A0] hover:bg-[#242424] hover:text-[#FAFAFA]'
    : 'border-[#E0E0DC] bg-[#F0F0ED] text-[#6B6B6B] hover:bg-[#E8E8E5] hover:text-[#0A0A0A]';
  const bulletRow = dark
    ? 'border-[#1E1E1E] bg-[#1C1C1C] text-[#C0C0C0]'
    : 'border-[#E8E8E5] bg-[#F7F7F5] text-[#3A3A3A]';

  return (
    <div className={`relative min-h-screen overflow-x-hidden [font-family:Inter,var(--font-ui),sans-serif] [overflow-anchor:none] ${pageBg}`}>

      {/* ── NAV ── */}
      <header className={`sticky top-0 z-40 border-b backdrop-blur-xl ${navBg}`}>
        <div className={`${CONTAINER} flex flex-wrap items-center justify-between gap-4 py-4`}>
          <a href="#top" className="inline-flex items-center gap-3" aria-label="Advoq home">
            <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl border shadow-sm ${dark ? 'border-[#2A2A2A] bg-white' : 'border-[#E8E8E5] bg-white'}`}>
              <BrandLogo variant="mark" className="h-6 w-auto" />
            </span>
            <span className={`text-xl font-semibold tracking-[-0.05em] ${textPri}`}>Advoq</span>
          </a>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              aria-label="Toggle theme"
              className={`inline-flex h-9 w-9 items-center justify-center rounded-full border transition ${toggleBtn}`}
            >
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <button
              type="button"
              data-testid="landing-login-btn"
              onClick={() => navigate('/auth')}
              className={`inline-flex items-center justify-center rounded-full border px-5 py-2.5 text-sm font-semibold transition ${ghostBtn}`}
            >
              Sign In
            </button>
            <button
              type="button"
              data-testid="landing-signup-btn"
              onClick={() => navigate('/auth')}
              className="inline-flex items-center justify-center rounded-full bg-[#C9A84C] px-5 py-2.5 text-sm font-semibold text-[#0A0A0A] transition hover:bg-[#D4B560]"
            >
              Get Access
            </button>
          </div>
        </div>
      </header>

      <main id="top" className="relative z-10">

        {/* ── HERO ── */}
        <section className="flex min-h-[calc(100vh-72px)] items-center py-14 sm:py-20">
          <div className={`${CONTAINER} grid gap-12 lg:grid-cols-[0.95fr_1.05fr] lg:items-center`}>
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#C9A84C]/25 bg-[#C9A84C]/10 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.26em] text-[#C9A84C]">
                <ShieldCheck className="h-3.5 w-3.5" />
                Built for high-stakes litigation
              </div>

              <h1 className={`mt-8 text-5xl font-semibold leading-[0.92] tracking-[-0.08em] sm:text-6xl lg:text-[5.4rem] ${textPri}`}>
                <span className="block">The Operating</span>
                <span className="block">System</span>
                <span className="block">for Indian Lawyers</span>
              </h1>

              <p className={`mt-8 max-w-xl text-lg leading-8 sm:text-xl ${textSec}`}>
                Matter intelligence, legal research, adversarial analysis, and practice management — unified in one platform.
              </p>

              <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                <a
                  href="mailto:info@advoq.in?subject=Request%20Demo%20-%20Advoq"
                  data-testid="hero-cta-primary"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C9A84C] px-6 py-3.5 text-sm font-semibold text-[#0A0A0A] transition hover:bg-[#D4B560]"
                >
                  Request Demo
                  <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href="#live-demo"
                  data-testid="hero-cta-secondary"
                  className={`inline-flex items-center justify-center gap-2 rounded-full border px-6 py-3.5 text-sm font-semibold transition ${ghostBtn}`}
                >
                  <PlayCircle className="h-4 w-4" />
                  See it live
                </a>
              </div>

              <div className={`mt-8 inline-flex flex-wrap items-center gap-3 text-sm ${textMuted}`}>
                <Languages className="h-4 w-4 text-[#C9A84C]" />
                <span>Built for Indian courts · Hindi, Tamil, Telugu supported</span>
              </div>
            </div>

            <DemoWindow demoRef={demoRef} activeDemo={activeDemo} typedPrompt={typedPrompt} dark={dark} />
          </div>
        </section>

        {/* ── SOCIAL PROOF BAR ── */}
        <section className={`border-y ${border} ${altBg}`}>
          <div className={`${CONTAINER} flex flex-col gap-5 py-5 lg:flex-row lg:items-center lg:justify-between`}>
            <p className={`text-sm font-medium tracking-[0.02em] ${textSec}`}>
              Trusted by advocates at Delhi HC · Bombay HC · Supreme Court
            </p>
            <div className="flex flex-wrap gap-2">
              {SOCIAL_PROOF_BADGES.map((b) => (
                <span key={b} className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] ${badge}`}>
                  {b}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ── PILLARS ── */}
        <section id="features" className={`py-20 sm:py-24 ${altBg}`}>
          <div className={CONTAINER}>
            <SectionHeader
              dark={dark}
              eyebrow="Three pillars"
              title="A premium operating layer for matters, lawyers, and the firm itself."
              body="Designed for the way serious Indian practices actually run: live matters, partner oversight, multilingual communication, and deep preparation before every hearing."
            />

            <div className="mt-12 grid gap-6 lg:grid-cols-3">
              {PILLARS.map((pillar) => {
                const Icon = pillar.icon;
                const iconBg = pillar.accent
                  ? 'bg-[#C9A84C] text-[#0A0A0A]'
                  : dark ? 'bg-[#FAFAFA] text-[#0A0A0A]' : 'bg-[#0A0A0A] text-white';

                return (
                  <article
                    key={pillar.name}
                    className={`rounded-2xl border p-8 transition hover:-translate-y-0.5 ${cardBg} ${
                      dark
                        ? 'shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
                        : 'shadow-[0_8px_32px_rgba(0,0,0,0.06)]'
                    }`}
                  >
                    <div className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ${iconBg}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <p className="mt-6 text-xs font-semibold uppercase tracking-[0.24em] text-[#C9A84C]">{pillar.name}</p>
                    <h3 className={`mt-3 text-xl font-semibold tracking-[-0.04em] ${textPri}`}>{pillar.title}</h3>
                    <p className={`mt-4 text-base leading-8 ${textSec}`}>{pillar.body}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── FEATURE SPOTLIGHT ── */}
        <section id="feature-spotlight" className={`py-20 sm:py-24`}>
          <div className={CONTAINER}>
            <SectionHeader
              dark={dark}
              eyebrow="Feature spotlight"
              title="Three capabilities that change how a firm prepares, decides, and executes."
              body="Each workflow is built to fit the real pace of chamber work: quick capture, adversarial thinking, and bench-aware preparation connected to the same matter."
            />

            <div className="mt-14 space-y-8">
              {SPOTLIGHT_ROWS.map((row) => (
                <article
                  key={row.id}
                  className={`rounded-2xl border p-6 sm:p-8 ${dark ? 'border-[#1E1E1E] bg-[#141414]' : 'border-[#E8E8E5] bg-white'}`}
                >
                  <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
                    <div className={row.reverse ? 'lg:order-2' : ''}>
                      <SpotlightVisual type={row.visual} dark={dark} />
                    </div>

                    <div className={row.reverse ? 'lg:order-1' : ''}>
                      <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#C9A84C]">{row.eyebrow}</p>
                      <h3 className={`mt-4 text-3xl font-semibold tracking-[-0.05em] ${textPri}`}>{row.title}</h3>
                      <p className={`mt-4 text-base leading-8 ${textSec}`}>{row.description}</p>

                      <div className="mt-8 grid gap-3">
                        {row.bullets.map((bullet) => (
                          <div
                            key={bullet}
                            className={`flex items-start gap-3 rounded-xl border px-4 py-4 text-sm leading-6 ${bulletRow}`}
                          >
                            <Check className="mt-1 h-4 w-4 flex-none text-[#C9A84C]" />
                            <span>{bullet}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── PRICING ── */}
        <section id="pricing" className={`py-20 sm:py-24 ${altBg}`}>
          <div className={CONTAINER}>
            <SectionHeader
              dark={dark}
              eyebrow="Pricing preview"
              title="Clear entry points for solo lawyers, growing practices, and full firms."
              body="Start with a 14-day free trial, then choose the operating layer that matches the way your chamber or firm actually works."
            />

            <div className="mt-12 flex justify-center">
              <article className="w-full max-w-sm rounded-2xl border border-[#C9A84C]/30 bg-[#0A0A0A] p-8 shadow-[0_16px_64px_rgba(0,0,0,0.24)]">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#C9A84C]">Pro</p>
                    <p className="mt-4 text-5xl font-semibold tracking-[-0.06em] text-[#FAFAFA]">₹1,499<span className="text-2xl font-medium text-[#A0A0A0]">/mo</span></p>
                    <p className="mt-3 text-base text-[#A0A0A0]">Everything you need to run a serious practice.</p>
                  </div>
                  <span className="rounded-full border border-[#C9A84C]/30 bg-[#C9A84C]/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#C9A84C]">
                    14-day trial
                  </span>
                </div>

                <div className="mt-8 space-y-3">
                  {PRO_FEATURES.map((feature) => (
                    <div key={feature} className="flex items-start gap-3 text-sm leading-6 text-[#A0A0A0]">
                      <Check className="mt-1 h-4 w-4 flex-none text-[#C9A84C]" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => navigate('/auth')}
                  className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#C9A84C] px-5 py-3.5 text-sm font-semibold text-[#0A0A0A] transition hover:bg-[#D4B560]"
                >
                  Get Access
                  <ArrowRight className="h-4 w-4" />
                </button>

                <p className={`mt-5 text-center text-xs ${textMuted}`}>
                  Questions?{' '}
                  <a href="mailto:info@advoq.in" className="underline decoration-[#C9A84C] underline-offset-4 hover:text-[#C9A84C] transition">
                    Talk to us
                  </a>
                </p>
              </article>
            </div>
          </div>
        </section>
      </main>

      {/* ── FOOTER ── */}
      <footer className={`border-t ${border} ${altBg}`}>
        <div className={`${CONTAINER} py-10`}>
          <div className="grid gap-10 lg:grid-cols-3">
            <div>
              <div className="inline-flex items-center gap-3">
                <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl border ${dark ? 'border-[#2A2A2A]' : 'border-[#E8E8E5]'} bg-white`}>
                  <BrandLogo variant="mark" className="h-6 w-auto" />
                </span>
                <span className={`text-xl font-semibold tracking-[-0.05em] ${textPri}`}>Advoq</span>
              </div>
              <p className={`mt-4 text-sm ${textMuted}`}>Built for Indian courts</p>
            </div>

            <div className={`flex flex-wrap gap-x-6 gap-y-3 text-sm ${textSec}`}>
              <a href="#features" className={`transition hover:${textPri}`}>Features</a>
              <a href="#pricing" className={`transition hover:${textPri}`}>Pricing</a>
              <a href="mailto:info@advoq.in?subject=Advoq%20Blog%20Updates" className={`transition hover:${textPri}`}>Blog</a>
              <a href="mailto:info@advoq.in" className={`transition hover:${textPri}`}>Contact</a>
            </div>

            <div className={`text-sm lg:text-right ${textMuted}`}>© 2025 Advoq · Privacy · Terms</div>
          </div>

          <div className={`mt-8 border-t pt-5 text-sm ${border} ${textMuted}`}>
            Made in India for Indian lawyers
          </div>
        </div>
      </footer>
    </div>
  );
}
